import { AxiosError } from 'axios';
import { ServerResponse } from 'http';
import { DateTime } from 'luxon';
import { NextPageContext } from 'next';
import Router from 'next/router';
import { parseCookies, setCookie } from 'nookies';
import queryString, { ParsedQuery } from 'query-string';

import { Size } from '../components/common/image';
import { PageError } from '../store/app/types';
import { QuizValues } from '../store/quiz/types';
import { ImageEntity } from '../types';
import { BASE_PATH, HOTJAR_ID, ONCHANGE_LIST } from './variables';

const handleRouteChange = (url: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hj = (window as any).hj;
  if (hj && process.browser) {
    hj('stateChange', url);
  }
};
if (HOTJAR_ID) {
  Router.events.on('routeChangeStart', handleRouteChange);
}

export const toCapitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export const nextRedirect = async (
  res: ServerResponse | null,
  href: string,
  as?: string,
  options: {
    scrollTop?: boolean;
    query?: ParsedQuery;
    shallow?: boolean;
  } = { scrollTop: false, query: {}, shallow: false },
): Promise<boolean | void | ServerResponse> => {
  const { scrollTop, query, shallow } = options;
  const isAbsolute = href.indexOf('http') === 0;
  const url = queryString.stringifyUrl({ url: `${BASE_PATH}${as || href}`, query });

  if (res) {
    res.writeHead(302, {
      Location: url,
    });
    return res.end();
  }
  if (isAbsolute) {
    return window.location.assign(url);
  }

  const promise = await Router.push({ pathname: href, query }, { pathname: as || href, query }, { shallow });

  if (scrollTop) {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      document.body.focus();
    }
  }

  return promise;
};

export const apiErrorToPageError = (error: AxiosError | Error | any): PageError => {
  if (error.response) {
    const {
      response: { statusText, status, data },
    }: AxiosError = error;

    return {
      code: status,
      message: data && data.message ? data.message : statusText,
    };
  }
  return {
    code: 500,
    message: error.message,
  };
};

export const isValue = (values: QuizValues, name: string): boolean =>
  Array.isArray(values[name]) ? values[name].length > 0 : !!values[name];

export const cleanObject = (obj: object) => {
  Object.keys(obj).forEach(
    (key) => (obj[key] === undefined || obj[key] === null || obj[key] === '') && delete obj[key],
  );
  return obj;
};

export const getBestThumbnail = (img: ImageEntity, size?: Size) => {
  if (size) {
    const sizes: Array<Size> = ['lg', 'md', 'sm'];
    const getSize = (size: Size) => {
      if (img.thumbnails[size]) {
        return img.thumbnails[size];
      } else {
        const i = sizes.indexOf(size);
        const newSize = sizes[i - 1];
        if (!newSize) {
          return img.url;
        }
        return getSize(newSize);
      }
    };
    return getSize(size);
  } else {
    return img.url;
  }
};

export const footerMessageFormatter = ({ onChange, values }: { onChange: string; values: string[] }) => {
  const isMatch = onChange.match(/%.+?%/gm);
  let result = onChange;

  if (isMatch) {
    isMatch.forEach((match) => {
      if (match.includes(ONCHANGE_LIST[0])) {
        const [date] = values;
        const end = DateTime.fromFormat(date, 'y-MM-dd');
        const start = DateTime.local();
        const daysLeft = end.diff(start, ['days']).toObject();

        if (match.includes(ONCHANGE_LIST[0])) {
          result = result.replace(match, `${Math.ceil(daysLeft.days)} day${daysLeft.days > 1 ? 's' : ''}`);
        }
      }
      if (match.includes(ONCHANGE_LIST[1])) {
        const vowel = ['a', 'e', 'i', 'o', 'u'];

        const isJoin = match.match(/join='.+?'/gm);
        const isArticle = match.includes('putArticle');

        const join = isJoin ? isJoin[0].replace("join='", '').slice(0, -1) : ' & ';

        const value = values
          .map((option) => {
            if (isArticle) {
              return `${vowel.includes(option.toLowerCase()[0]) ? 'An' : 'A'} ${option}`;
            }
            return option;
          })
          .join(join);

        result = result.replace(match, value);
      }
    });
  }

  return result;
};

export const RESULT_COOKIE = 'lastResult';
export const setResultCookie = (link: string) => {
  setCookie(null, RESULT_COOKIE, link, {
    maxAge: 10 * 365 * 30 * 24 * 60 * 60,
    path: BASE_PATH || '/',
  });
};

export const getResultCookie = (ctx?: NextPageContext) => {
  const cookies = parseCookies(ctx);
  return cookies[RESULT_COOKIE];
};
